<template>
  <div class="h-100">
    <vis-toolbar class="vis-treeview-toolbar border-bottom" :items="toolbar" />
    <vis-loading v-if="loading" :loading="loading" class="h-100" />
    <vis-local-tree
      v-else
      :items="items"
      @select="onSelect"
      class="vis-has-toolbar py-3 overflow-auto"
    >
    </vis-local-tree>

    <vis-mnemonic-create-view
      v-if="showCreateDialog"
      :entityType="entityType"
      :visible="showCreateDialog"
      @ok="saveMnemonic"
      @hide="hideCreate"
    />

    <vis-type-create-view
      v-if="showCreateTypeDialog"
      :visible="showCreateTypeDialog"
      @hide="hideCreateType"
      @ok="createType"
    />
    <vis-create-mnemonic-copy-view
      v-if="showCreateMnemonicCopyDialog"
      :visible="showCreateMnemonicCopyDialog"
      @hide="hideCreateCopy"
      @ok="createMnemonicCopy"
    />

    <vis-confirm-dialog
      :visible="rebuildDialog.visible"
      :title="rebuildDialog.title"
      :text="rebuildDialog.text"
      @acceptChanges="rebuildDialog.accept"
      @cancelChanges="rebuildDialog.cancel"
      @hideDialog="rebuildDialog.cancel"
    ></vis-confirm-dialog>

    <vis-confirm-dialog
      :visible="deleteDialog.visible"
      :title="deleteDialog.title"
      :text="deleteDialog.text"
      @acceptChanges="deleteDialog.accept"
      @cancelChanges="deleteDialog.cancel"
      @hideDialog="deleteDialog.cancel"
    ></vis-confirm-dialog>

    <vis-confirm-dialog
      :visible="deleteTypeDialog.visible"
      :title="deleteTypeDialog.title"
      :text="deleteTypeDialog.text"
      @acceptChanges="deleteTypeDialog.accept"
      @cancelChanges="deleteTypeDialog.cancel"
      @hideDialog="deleteTypeDialog.cancel"
    ></vis-confirm-dialog>
  </div>
</template>

<script>
import api from "@/api/visary-api";
import NodeTypes from "./js/node-types";
import Mapper from "./js/mapper";

import VisMnemonicCreateView from "./vis-mnemonic-create-view";
import VisMnemonicTreeItem from "./vis-mnemonic-tree-item";
import VisTypeCreateView from "./vis-type-create-view";
import VisCreateMnemonicCopyView from "./vis-create-mnemonic-copy";
import treeArrayUtils from '@/views/base/common/js/tree-array-utils';
import errorMessage from "@/shared/error-message";
const mnemonic = "MnemonicEntity";

export default {
  name: "vis-entity-type-mnemonics-tree",
  components: {
    VisMnemonicCreateView,
    VisMnemonicTreeItem,
    VisTypeCreateView,
    VisCreateMnemonicCopyView,
  },
  data(vm) {
    return {
      loading: false,
      showCreateDialog: false,
      showCreateTypeDialog: false,
      showCreateMnemonicCopyDialog: false,
      selectedItem: null,
      items: null,
      entityType: null,
      search: null,
      rebuildDialog: {
        visible: false,
        title: vm.$t("confirmDialog.rebuildMnemonic.title"),
        text: vm.$t("confirmDialog.rebuildMnemonic.text"),
        async accept() {
          try {
            await api.mnemonic.rebuild();
          } catch (ex) {
            const error = await errorMessage(ex);
            vm.$visNotification.show(error.title, error.message, "danger");
          } finally {
            vm.rebuildDialog.visible = false;
          }
        },
        cancel() {
          vm.rebuildDialog.visible = false;
        },
      },
      deleteDialog: {
        visible: false,
        title: vm.$t("confirmDialog.deleteMnemonic.title"),
        text: vm.$t("confirmDialog.deleteMnemonic.text"),
        async accept() {
          await api.crud.delete(mnemonic, vm.selectedItem.id);

          treeArrayUtils.removeItem(vm.selectedItem, vm.items, vm.applyChanges);

          vm.onSelect(null);
          vm.deleteDialog.visible = false;
        },
        cancel() {
          vm.deleteDialog.visible = false;
        },
      },
      deleteTypeDialog: {
        visible: false,
        title: vm.$t("confirmDialog.deleteMnemonic.title"),
        text: vm.$t("confirmDialog.deleteMnemonic.text"),
        async accept() {
          const typeTitle = vm.selectedItem.original.Title;
          await api.mnemonic.deleteType(typeTitle);
          treeArrayUtils.removeItem(vm.selectedItem, vm.items, vm.applyChanges);
          vm.onSelect(null);
          vm.deleteTypeDialog.visible = false;
        },
        cancel() {
          vm.deleteTypeDialog.visible = false;
        },
      },
      toolbar: [
        {
          type: "button-group",
          buttons: [
            {
              id: "add-child",
              icon: "far fa-plus-circle",
              title: this.$t("add"),
              order: 10,
              disabled: true,
              onClick: this.onCreateNew,
            },
            {
              id: "delete",
              icon: "far fa-trash",
              title: this.$t("delete"),
              order: 30,
              disabled: true,
              hidden: false,
              onClick: () => {
                if (this.selectedItem && this.selectedItem.original.IsDynamic) {
                  this.deleteTypeDialog.visible = true;
                } else {
                  this.deleteDialog.visible = true;
                }
              },
            },
            {
              id: "rebuild",
              icon: "far fa-undo",
              title: this.$t("rebuild"),
              order: 100,
              disabled: false,
              hidden: false,
              onClick: () => {
                this.rebuildDialog.visible = true;
              },
            },
            {
              icon: "fal fa-question",
              type: "vis-mnemonic-help-toolbar-button",
              mnemonic: "MnemonicSettingsHelpFile",
              title: this.$t("toolbar.button.Help"),
            }
          ],
        },
      ],
    };
  },
  methods: {
    applyChanges() {
      this.items = [].concat(this.items);
    },
    hideCreate() {
      this.showCreateDialog = false;
    },
    hideCreateCopy(isValid) {
      if (isValid) {
        this.showCreateMnemonicCopyDialog = false;
      }
    },
    hideCreateType(isValid) {
      if (isValid) {
        this.showCreateTypeDialog = false;
      }
    },
    onSelect: function (items) {
      this.selectedItem = items && items.length ? items[0] : null;
      this.updateToolbarState();
      this.$emit("select", this.selectedItem);
    },
    updateToolbarState() {
      this.toolbar.forEach((t) => {
        if (t.buttons) {
          const itemSelected = this.selectedItem != null;
          const isAssembly = this.selectedItem?.nodeType === NodeTypes.assembly;
          const hasGeneratedTypes = this.selectedItem?.original?.HasGeneratedTypes === true;
          const isEntityType = this.selectedItem?.nodeType === NodeTypes.entityType;
          const isDynamic = this.selectedItem?.original?.IsDynamic === true;
          const isDynamicObjectAssembly = this.selectedItem?.parent?.original?.Name !== "Visary.DynamicObject"

          t.buttons.forEach((b) => {
            switch (b.id) {
              case "add-child":
                b.disabled =
                  !itemSelected ||
                  (isAssembly && !hasGeneratedTypes) ||
                  (isEntityType && isDynamic);
                break;
              case "delete":
                b.disabled =
                  !itemSelected ||
                  isAssembly ||
                  (isEntityType && (isDynamicObjectAssembly || !isDynamic));
                break;
            }
          });
        }
      });
    },
      async saveMnemonic(model) {
      let res = await api.mnemonic.createNewMnemonic(model);
      if (this.selectedItem) {
        const node = Mapper.makeMnemonicItem(res, this.selectedItem);
        this.selectedItem.children.push(node);
        this.selectedItem.expanded = true;
        treeArrayUtils.updateItem(this.selectedItem, this.items, this.applyChanges)
        this.onSelect([node]);
      }
    },
    async createType(model) {
      try {
        let res = await api.mnemonic.createType(model);
        if (this.selectedItem) {
          const node = Mapper.makeTypeItem(res, this.selectedItem);
          this.selectedItem.children.push(node);
          this.selectedItem.expanded = true;
          treeArrayUtils.updateItem(this.selectedItem, this.items, this.applyChanges)
          this.onSelect([node]);
        }
      } catch (ex) {
        this.$visNotification.error(ex.message);
      }
    },
    async createMnemonicCopy(model) {
      model.ParentMnemonic = this.selectedItem.original.Mnemonic;
      let res = await api.mnemonic.createMnemonicCopy(model);
      if (this.selectedItem) {
        const node = Mapper.makeMnemonicItem(res, this.selectedItem.parent);
        this.selectedItem.parent.children.push(node);
        treeArrayUtils.updateItem(this.selectedItem, this.items, this.applyChanges)
        this.onSelect([node]);
      }
    },
    onCreateNew() {
      if (
        this.selectedItem &&
        this.selectedItem.nodeType === NodeTypes.entityType
      ) {
        this.entityType = this.selectedItem.id;
        this.showCreateDialog = true;
      } else if (
        this.selectedItem &&
        this.selectedItem.original.HasGeneratedTypes
      ) {
        this.showCreateTypeDialog = true;
      } else if (
        this.selectedItem &&
        this.selectedItem.nodeType === NodeTypes.mnemonic
      ) {
        this.showCreateMnemonicCopyDialog = true;
      }
    },
    async fetchData() {
      this.loading = true;
      let data = await api.mnemonic.getEntityTypes();

      this.items = data.map(Mapper.makeAssymblyItem);
      this.loading = false;
    },
    updateEntity(entity) {
      let item;

      switch (this.selectedItem.nodeType) {
        case NodeTypes.assembly:
          item = Mapper.makeAssymblyItem(entity);
          break;
        case NodeTypes.entityType:
          item = Mapper.makeTypeItem(entity, this.selectedItem.parent);
          break;
        case NodeTypes.mnemonic:
          item = Mapper.makeMnemonicItem(entity, this.selectedItem.parent);
          break;
      }

      this.selectedItem.title = item.title;
      this.selectedItem.icon = item.icon;
      this.selectedItem.original = Object.assign(
        this.selectedItem.original,
        entity
      );
      treeArrayUtils.updateItem(this.selectedItem, this.items, this.applyChanges)
    },
  },
  async created() {
    await this.fetchData();
  },
};
</script>
